import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, ArrowUpRight, Sparkles } from "lucide-react";
import { scrollToForm, trackEvent, trackPlanSelected, trackDiyReferralClick, SERVICE } from "@/lib/tracking";
import DiyAccessModal from "@/components/website/DiyAccessModal";

export const WA_PLANS = [
  {
    id: "dfy",
    tag: "Done for you",
    name: "Done-for-You WhatsApp Automation Setup",
    price: "R10,850",
    period: "once-off",
    description: "Rekisa plans, builds, tests and launches a working WhatsApp automation for your enquiries.",
    includes: [
      "WhatsApp automation planning and customer journey mapping",
      "Chatfuel flow setup for your agreed use case",
      "Automated replies and FAQ conversations",
      "Lead capture and basic qualification questions",
      "Human handover path to your team",
      "Testing and launch support",
    ],
    notIncluded: "Ongoing monthly management, advanced CRM integrations and custom API work are quoted separately.",
    highlight: true,
    color: "#25d366",
    eventKey: "pricing_plan_selected_dfy_setup",
  },
  {
    id: "dwy",
    tag: "Done with you",
    name: "Done-with-You Automation Enablement",
    price: "R4,850",
    period: "per month",
    description: "For teams that want to build and own the automation themselves, with Rekisa guiding the setup.",
    includes: [
      "Strategy and customer journey guidance",
      "Chatfuel setup and flow-structure guidance",
      "Lead capture and FAQ guidance",
      "Monthly review session and recommendations",
      "Training to manage and update the flow",
    ],
    notIncluded: "Rekisa does not build the automation for you on this option.",
    color: "#4285f4",
    eventKey: "pricing_plan_selected_dwy",
  },
];


function PlanCard({ plan, onSelect }) {
  const hl = plan.highlight;
  return (
    <div data-testid={`wa-pricing-card-${plan.id}`}
      className={`relative rounded-3xl p-7 sm:p-8 flex flex-col ${hl ? "bg-gradient-to-b from-[#25d366]/10 to-white border-2 border-[#25d366] shadow-[0_30px_80px_-30px_rgba(37,211,102,0.4)]" : "border border-zinc-200 bg-white"}`}>
      {hl && (
        <div className="absolute -top-3 left-7 inline-flex items-center gap-1.5 rounded-full bg-[#25d366] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
          <Sparkles className="h-3 w-3" /> Recommended
        </div>
      )}
      <div className="flex items-center gap-2 mb-3">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: plan.color }} />
        <span className="text-[11px] tracking-[0.18em] uppercase font-mono text-zinc-500 font-semibold">{plan.tag}</span>
      </div>
      <h3 className="font-display text-2xl font-black text-zinc-900 tracking-tight">{plan.name}</h3>
      <div className="mt-4 flex items-baseline gap-2 flex-wrap" data-testid={`wa-pricing-price-${plan.id}`}>
        <span className="text-xs text-zinc-500 uppercase tracking-widest font-mono">From</span>
        <span className={`font-display font-black text-zinc-900 tracking-tight ${hl ? "text-5xl" : "text-4xl"}`}>{plan.price}</span>
        <span className="text-zinc-500 text-sm">{plan.period}</span>
      </div>
      <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed">{plan.description}</p>
      <ul className="mt-6 space-y-2.5 flex-1">
        {plan.includes.map((item) => (
          <li key={item} className="flex items-start gap-2.5 text-sm text-zinc-800">
            <span className="h-5 w-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${plan.color}18`, color: plan.color }}>
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            {item}
          </li>
        ))}
      </ul>
      <p className="mt-5 text-xs text-zinc-500 leading-relaxed">{plan.notIncluded}</p>
      <Button onClick={() => onSelect(plan)} data-testid={`wa-pricing-cta-${plan.id}`}
        className={`mt-6 whitespace-normal h-auto min-h-12 py-3 text-center rounded-full text-base font-semibold ${hl ? "bg-[#25d366] hover:bg-[#1ebe5d] text-[#04220f] shadow-[0_8px_24px_rgba(37,211,102,0.4)]" : "bg-zinc-900 hover:bg-black text-white"}`}>
        Request an Implementation Quote <ArrowUpRight className="ml-1 h-5 w-5" />
      </Button>
    </div>
  );
}

export default function WhatsappPricing({ setSelectedPlan }) {
  const [diyOpen, setDiyOpen] = useState(false);

  const handleSelect = (plan) => {
    setSelectedPlan(plan.id);
    trackEvent(plan.eventKey, { plan: plan.id });
    trackEvent("cta_click", { location: "wa_pricing", plan: plan.id });
    trackPlanSelected({ service: SERVICE.whatsapp, planId: plan.id });
    scrollToForm();
  };

  const handleDiy = () => {
    trackEvent("pricing_plan_selected_diy_chatfuel", { plan: "diy" });
    trackEvent("diy_access_request_open", { platform: "chatfuel", source: "pricing_link" });
    trackDiyReferralClick({ service: SERVICE.whatsapp, partnerName: "Chatfuel", referralUrl: "https://chatfuel.com/?fpr=thato21" });
    setDiyOpen(true);
  };

  return (
    <section id="pricing" data-testid="wa-pricing-section" className="relative py-24 sm:py-32 bg-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl mb-12">
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">WhatsApp automation pricing South Africa</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
            WhatsApp Automation Implementation Pricing
          </h2>
          <p className="mt-5 text-zinc-600 text-lg leading-relaxed">
            The cost of WhatsApp automation depends on what your business needs, the complexity of the conversations and the
            systems that must be connected. Rekisa offers implementation and support options based on your requirements.
          </p>
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-6 items-stretch">
          {WA_PLANS.map((p) => <PlanCard key={p.id} plan={p} onSelect={handleSelect} />)}
        </div>

        <div data-testid="wa-pricing-note" className="mt-8 rounded-2xl border border-[#fbbc04]/40 bg-[#fbbc04]/[0.08] p-5 text-sm text-zinc-800 leading-relaxed">
          <span className="font-bold text-zinc-900">Separate costs: </span>
          Prices start from the amounts shown and are confirmed in your quotation. Additional integrations may require a custom
          quotation. Chatfuel subscription fees and applicable WhatsApp messaging charges are billed separately and paid directly
          by your business, unless your quotation states otherwise.
        </div>

        <p className="mt-8 text-sm text-zinc-500">
          Prefer to do it yourself?{" "}
          <button type="button" onClick={handleDiy} data-testid="wa-pricing-cta-diy" className="font-semibold text-zinc-700 underline underline-offset-4 hover:text-zinc-900">
            Start directly on Chatfuel
          </button>{" "}
          and manage your own setup, billing and optimisation.
        </p>
      </div>

      <DiyAccessModal open={diyOpen} onClose={() => setDiyOpen(false)} platform="chatfuel" platformLabel="Chatfuel" accent="#25d366" />
    </section>
  );
}
